"use client";

import { useEffect, useMemo, useState } from "react";

import { useParams } from "next/navigation";

import { useTranslation } from "~/app/hooks/useTranslation";
import { submitDemoRequest } from "~/common/api/demoRequestClient";
import Modal from "~/common/components/Modal";
import PhoneInputField from "~/common/components/PhoneInputField";
import Input from "~/common/components/Input";
import {
  LEAD_CAPTURE_MODAL_VARIANTS,
  OPEN_LEAD_CAPTURE_MODAL_EVENT,
  getLeadCaptureRequestType,
} from "~/common/lib/leadCaptureModal";
import { getEmailError, normalizeEmail } from "~/common/utils/email";
import { formatPhoneInput, getDefaultPhoneCountry, isPhoneValid, normalizePhoneNumber } from "~/common/utils/phone";

import styles from "./LeadCaptureModalProvider.module.scss";

const EMPTY_FORM = {
  email: "",
  phone: "",
};

function compactString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function resolveVariant(config) {
  return config?.variant === LEAD_CAPTURE_MODAL_VARIANTS.GET_AN_OFFER
    ? LEAD_CAPTURE_MODAL_VARIANTS.GET_AN_OFFER
    : LEAD_CAPTURE_MODAL_VARIANTS.BOOK_DEMO;
}

function getPrefill(config) {
  if (!config?.prefill || typeof config.prefill !== "object") {
    return { ...EMPTY_FORM };
  }
  return {
    ...EMPTY_FORM,
    ...Object.fromEntries(
      Object.entries(config.prefill).map(([key, value]) => [
        key,
        typeof value === "string" ? value : EMPTY_FORM[key] || "",
      ])
    ),
  };
}

export default function LeadCaptureModalProvider() {
  const { t } = useTranslation();
  const params = useParams();
  const locale = typeof params?.locale === "string" ? params.locale : undefined;
  const contactRequiredError = t("leadCaptureModal.errors.contactRequired");

  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM }));
  const [errors, setErrors] = useState({});
  const [phoneCountry, setPhoneCountry] = useState(() => getDefaultPhoneCountry(locale));

  const variant = resolveVariant(config);

  const modalContent = useMemo(() => {
    if (variant === LEAD_CAPTURE_MODAL_VARIANTS.GET_AN_OFFER) {
      return {
        title: config?.title || t("leadCaptureModal.getAnOffer.title"),
        description: config?.description || t("leadCaptureModal.getAnOffer.description"),
        submitLabel: config?.submitLabel || t("leadCaptureModal.getAnOffer.submit"),
      };
    }

    return {
      title: config?.title || t("leadCaptureModal.bookDemo.title"),
      description: config?.description || t("leadCaptureModal.bookDemo.description"),
      submitLabel: config?.submitLabel || t("leadCaptureModal.bookDemo.submit"),
    };
  }, [config, t, variant]);

  useEffect(() => {
    const handleOpen = (event) => {
      const detail = event?.detail && typeof event.detail === "object" ? event.detail : {};
      const nextLocale = typeof detail.phoneCountry === "string" ? detail.phoneCountry : getDefaultPhoneCountry(locale);

      setConfig(detail);
      setOpen(true);
      setSubmitted(false);
      setSubmitting(false);
      setErrors({});
      setPhoneCountry(nextLocale);
      setFormData(getPrefill(detail));
    };

    window.addEventListener(OPEN_LEAD_CAPTURE_MODAL_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_LEAD_CAPTURE_MODAL_EVENT, handleOpen);
  }, [locale]);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    setSubmitted(false);
    setSubmitting(false);
    setErrors({});
    setConfig(null);
    setFormData({ ...EMPTY_FORM });
    setPhoneCountry(getDefaultPhoneCountry(locale));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? formatPhoneInput(value, phoneCountry) : value,
    }));
    setErrors((prev) => {
      const nextErrors = {
        ...prev,
        [name]: null,
        form: null,
      };

      if (name === "email" || name === "phone") {
        if (prev.email === contactRequiredError) {
          nextErrors.email = null;
        }
        if (prev.phone === contactRequiredError) {
          nextErrors.phone = null;
        }
      }

      return nextErrors;
    });
  };

  const handleCountryChange = (nextCountry) => {
    setPhoneCountry(nextCountry);
    setFormData((prev) => ({
      ...prev,
      phone: prev.phone ? formatPhoneInput(prev.phone, nextCountry) : prev.phone,
    }));
    setErrors((prev) => ({
      ...prev,
      phone: prev.phone === contactRequiredError ? null : prev.phone,
      email: prev.email === contactRequiredError ? null : prev.email,
      form: null,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const normalizedEmail = normalizeEmail(formData.email);
    const normalizedPhone = normalizePhoneNumber(formData.phone, phoneCountry);

    const nextErrors = {};

    if (!normalizedEmail && !normalizedPhone) {
      nextErrors.email = contactRequiredError;
      nextErrors.phone = contactRequiredError;
    }
    const emailError = normalizedEmail ? getEmailError(normalizedEmail) : null;
    if (emailError) {
      nextErrors.email = t("common.errors.invalidEmail");
    }
    if (normalizedPhone && !isPhoneValid(formData.phone, phoneCountry)) {
      nextErrors.phone = t("common.errors.invalidPhone");
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    try {
      setSubmitting(true);
      setErrors({});

      const requestType = config?.requestType || getLeadCaptureRequestType(variant);
      const customerInfo = {
        company: null,
        source: compactString(config?.source),
        language: locale,
        full_name: null,
        extra_info: null,
      };
      const payload = {
        context: "lead_capture_modal",
        modal_variant: variant,
        source: compactString(config?.source),
        ...(config?.payload && typeof config.payload === "object" && !Array.isArray(config.payload)
          ? config.payload
          : {}),
      };

      await submitDemoRequest({
        requestType,
        email: normalizedEmail,
        phoneNumber: normalizedPhone || null,
        customerInfo,
        payload,
      });

      setSubmitted(true);
      setFormData({ ...EMPTY_FORM });
      setErrors({});
    } catch (error) {
      if (error?.validation && typeof error.validation === "object") {
        const validation = { ...error.validation };
        if (validation.email === "invalid_email") validation.email = t("common.errors.invalidEmail");
        setErrors(validation);
      } else {
        setErrors({ form: error?.message || "Error" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      idPrefix="lead-capture-modal"
      title={submitted ? t("common.thankYou") : modalContent.title}
      open={open}
      onClose={handleClose}
      closeOnBackdrop={!submitting}
      panelClassName={styles.panel}
      className={styles.modalMain}
      footer={
        submitted ? (
          <button btntype="default" onClick={handleClose}>
            {t("common.ok")}
          </button>
        ) : null
      }
    >
      {submitted ? (
        <div className={styles.successState}>
          <p className={styles.successText}>{config?.successMessage || t("leadCaptureModal.success.description")}</p>
        </div>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <p className={styles.description}>{modalContent.description}</p>

          <div className={styles.fields}>
            <Input
              id="lead-capture-email"
              name="email"
              type="email"
              label={locale === "en" ? "Email" : "E-posta"}
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              autoComplete="email"
            />

            <div className={styles.orSeparator}>
              <span className={styles.orLine} />
              <span className={styles.orText}>{t("leadCaptureModal.orSeparator")}</span>
              <span className={styles.orLine} />
            </div>

            <PhoneInputField
              id="lead-capture-phone"
              name="phone"
              label={t("leadCaptureModal.fields.phone")}
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
              locale={locale}
              country={phoneCountry}
              onCountryChange={handleCountryChange}
              inputMode="tel"
              autoComplete="tel"
              searchPlaceholder={t("common.phoneCountrySelect.searchPlaceholder")}
              emptyLabel={t("common.phoneCountrySelect.emptyLabel")}
            />
          </div>

          <div className={styles.footer}>
            {errors.form ? <div className={styles.errorText}>{errors.form}</div> : null}
            <button type="submit" className={styles.submitButton} disabled={submitting}>
              {submitting ? t("common.submitting") || "..." : modalContent.submitLabel}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
